import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { TEST_IDS } from "@/lib/testIds";
import api from "@/lib/api";
import usePageSeo from "@/hooks/usePageSeo";
import { isNative, nativePlatform, nativeGoogleSignIn, appleSignIn } from "@/lib/native";

export default function Auth() {
  const [mode, setMode] = useState("login");
  usePageSeo({
    title: "Sign in or create your account — Dividend Portfolio Planner",
    description: "Sign in to DPP or create a free account in seconds. Save portfolios, backtest, and sync holdings. No credit card.",
    path: "/auth",
    noindex: true,
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");  // Persistent inline error — toast alone was invisible in ~12 rage-click sessions
  const [refCode, setRefCode] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [requiredPolicies, setRequiredPolicies] = useState([]); // [{slug, title, current_version, has_file}]
  const [aiQuotaIntent, setAiQuotaIntent] = useState(false);
  const { login, register, refreshUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [socialBusy, setSocialBusy] = useState(false);

  const socialSignIn = async (fn) => {
    setSocialBusy(true);
    try {
      await fn();
      await refreshUser();
      toast.success("Welcome");
      nav("/dashboard", { replace: true });
    } catch (e) {
      // 1001 = user closed the Apple sheet.
      if (!String(e?.code || e?.message || "").includes("1001")) {
        toast.error(e?.response?.data?.detail || "Sign-in didn't complete. Please try again.");
      }
    } finally {
      setSocialBusy(false);
    }
  };

  useEffect(() => {
    // Persist a ?ref=CODE from any entry point so it survives the login/register toggle.
    const params = new URLSearchParams(loc.search);
    const ref = params.get("ref");
    if (ref) {
      setRefCode(ref.toUpperCase());
      setMode("register");
    }
    // Land in Create-Account mode directly when the visitor was bounced here
    // from the "Save my first portfolio" flow (2026-02-13) — skips the extra
    // "Sign in / Create an account" toggle click at the moment of highest
    // conversion intent. Also supports an explicit `?mode=register` override.
    const modeParam = params.get("mode");
    const intent = params.get("intent");
    if (modeParam === "register" || intent === "save-portfolio" || intent === "ai-quota") {
      setMode("register");
    }
    if (intent === "ai-quota") setAiQuotaIntent(true);
  }, [loc.search]);

  useEffect(() => {
    // Fetch the set of required policies + their current versions so we can
    // send the exact accepted_versions map on register.
    if (mode !== "register") return;
    (async () => {
      try {
        const r = await api.get("/trust/documents");
        const required = (r.data.documents || []).filter((d) => d.required_for_signup);
        setRequiredPolicies(required);
      } catch (err) {
        // Non-fatal — we'll fall back to an empty map; the backend enforces anyway.
        setRequiredPolicies([]);
      }
    })();
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    // Guardrail: block double-submit even if a re-render slips the button back
    // through — 12 rage-click sessions in analytics showed users clicking up
    // to 9 times when the visual state didn't confirm the action.
    if (loading) return;
    setError("");
    if (mode === "register" && !tosAccepted) {
      setError("Please accept the Terms of Service and Privacy Policy to continue");
      toast.error("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }
    if (!email.trim() || !password) {
      setError(email.trim() ? "Enter your password" : "Enter your email address");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        const acceptedVersions = {};
        requiredPolicies.forEach((p) => { acceptedVersions[p.slug] = p.current_version; });
        await register(email, password, name, refCode, acceptedVersions);
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");

      // Auto-save advisor seed: if the visitor clicked "Save as my portfolio"
      // on the landing AI Advisor, we stashed their AI-suggested tickers in
      // localStorage. After successful register (or login), create that
      // portfolio for them so their pre-signup research becomes their
      // first saved artifact instead of disposable exploration.
      let redirectAfterSeed = null;
      try {
        const seedRaw = localStorage.getItem("dp_advisor_seed");
        if (seedRaw && mode === "register") {
          const seed = JSON.parse(seedRaw);
          if (seed?.tickers?.length) {
            const per = Number((100 / seed.tickers.length).toFixed(2));
            const holdings = seed.tickers.map((t, i, a) => ({
              ticker: t,
              weight: i === a.length - 1
                ? Number((100 - per * (a.length - 1)).toFixed(2))
                : per,
            }));
            const name = seed.tickers.slice(0, 3).join(" + ") + (seed.tickers.length > 3 ? "…" : "");
            // Fill required PortfolioCreate fields with sensible defaults —
            // 5-year lookback + $10k initial investment matches the same
            // defaults the manual portfolio builder uses.
            const now = new Date();
            const start = new Date(now); start.setFullYear(now.getFullYear() - 5);
            const iso = (d) => d.toISOString().slice(0, 10);
            const saved = await api.post("/portfolios", {
              name,
              description: `Auto-saved from AI Advisor · "${(seed.question || "").slice(0, 120)}"`,
              holdings,
              initial_investment: 10000,
              start_date: iso(start),
              end_date: iso(now),
              drip: true,
            });
            localStorage.removeItem("dp_advisor_seed");
            // Track the funnel step so we can measure advisor→signup→save.
            try {
              api.post("/landing/advisor/track", {
                event: "portfolio_saved",
                question: seed.question,
                tickers: seed.tickers,
              }).catch(() => {});
            } catch {}
            toast.success("We saved your AI picks as your first portfolio", { duration: 6000 });
            redirectAfterSeed = `/portfolio/${saved?.data?.id || ""}`;
          }
        }
      } catch (e) {
        // Non-fatal — user still gets a normal signup, just no auto-save.
        console.warn("advisor seed auto-save skipped:", e);
      }

      // Respect ?redirect=... or ?next=... so anon flows (save-portfolio,
      // ai-quota) bounce back to where the user was after auth. Advisor seed
      // wins if present.
      const params = new URLSearchParams(loc.search);
      const redirect = redirectAfterSeed || params.get("redirect") || params.get("next");
      nav(redirect && redirect.startsWith("/") ? redirect : "/dashboard");
    } catch (err) {
      // Show the error persistently (inline) AND as a toast — the toast alone
      // was disappearing before users noticed it, causing the rage-click loop.
      const detail = err?.response?.data?.detail || "Authentication failed — please try again";
      setError(detail);
      toast.error(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-9rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBwbGFubmluZyUyMHdlYWx0aHxlbnwwfHx8fDE3ODIzNzk3NDl8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Dividend Portfolio Planner" className="w-12 h-12 rounded-lg object-cover ring-1 ring-white/20" />
            <div>
              <div className="serif text-2xl">Dividend Portfolio Planner</div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] opacity-70">Plan · Analyze · Build income</div>
            </div>
          </div>
          <div>
            <h2 className="serif text-5xl leading-[1.05]">Grow income you understand.</h2>
            <p className="mt-4 opacity-80 max-w-md">Save unlimited portfolio scenarios, backtest them against decades of dividend history, and let AI explain the results.</p>
          </div>
          <div className="mono text-xs opacity-70">Educational tool · Not investment advice</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="serif text-4xl">
            {mode === "login" ? "Welcome back." : "Create your account for free."}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mode === "login" ? "Sign in to open your dividend workbench." : "No credit card. Start building scenarios in under a minute."}
          </p>

          {aiQuotaIntent && mode === "register" && (
            <div
              data-testid="auth-ai-quota-banner"
              className="mt-4 rounded-lg border border-secondary/30 bg-secondary/10 px-4 py-3 text-sm"
            >
              <div className="mono text-[10px] uppercase tracking-widest text-secondary">Keep exploring</div>
              <p className="mt-1 text-foreground/85">
                You used your free AI action. Create a free account (no card) to unlock <b>4 AI actions every month</b>, save your work, and pick up right where you left off.
              </p>
            </div>
          )}

          <Card className="mt-8">
            <CardContent className="p-6">
              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              {isNative() && nativePlatform() === "ios" && (
                <Button
                  type="button"
                  data-testid="apple-sign-in-btn"
                  disabled={socialBusy}
                  onClick={() => socialSignIn(appleSignIn)}
                  className="w-full rounded-full bg-black text-white hover:bg-black/85 mb-3"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M16.37 1.43c0 1.14-.42 2.2-1.2 3.04-.86.93-1.95 1.47-3.13 1.38-.1-1.1.44-2.24 1.17-3.02.82-.88 2.2-1.52 3.16-1.4zM20.5 17.1c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.1 3.51-1.53.02-1.93-1-4-.98-2.08.01-2.51 1-4.05.98-1.72-.02-3.04-1.77-4.03-3.33C.02 15.9-.27 10.86 1.46 8.2 2.69 6.3 4.63 5.2 6.45 5.2c1.86 0 3.03 1.02 4.56 1.02 1.49 0 2.4-1.02 4.55-1.02 1.62 0 3.34.88 4.57 2.41-4.02 2.2-3.37 7.94.37 9.49z"/>
                  </svg>
                  Sign in with Apple
                </Button>
              )}
              <Button
                type="button"
                data-testid={TEST_IDS.googleSignIn}
                variant="outline"
                disabled={socialBusy}
                className="w-full rounded-full border-border"
                onClick={() => {
                  if (isNative()) { nativeGoogleSignIn(); return; }
                  const redirectUrl = window.location.origin + "/dashboard";
                  window.location.href =
                    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
                }}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.24 1.4-1.68 4.1-5.4 4.1-3.25 0-5.9-2.68-5.9-6s2.65-6 5.9-6c1.85 0 3.09.79 3.8 1.46l2.6-2.5C16.62 3.4 14.55 2.4 12 2.4 6.86 2.4 2.7 6.56 2.7 11.7S6.86 21 12 21c6.93 0 9.32-4.86 9.32-8.83 0-.59-.06-1.05-.14-1.5H12z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <span className="mono uppercase tracking-widest">or with email</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && refCode && (
                  <div className="rounded-md border border-secondary/30 bg-secondary/10 px-3 py-2 text-sm">
                    <span className="mono text-[10px] uppercase tracking-widest text-secondary">Referred by</span>{" "}
                    <span className="mono font-semibold ml-1" data-testid="auth-ref-code">{refCode}</span>
                    <div className="text-xs text-muted-foreground mt-1">
                      When you upgrade to Pro, your friend gets a free month.
                    </div>
                  </div>
                )}
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      data-testid={TEST_IDS.registerName}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ada Lovelace"
                      required
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid={mode === "login" ? TEST_IDS.loginEmail : TEST_IDS.registerEmail}
                    value={email}
                    onChange={(e) => { setError(""); setEmail(e.target.value); }}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {mode === "login" && (
                      <Link
                        to="/forgot-password"
                        data-testid="auth-forgot-link"
                        className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-secondary transition-colors"
                      >
                        Forgot?
                      </Link>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    data-testid={mode === "login" ? TEST_IDS.loginPassword : TEST_IDS.registerPassword}
                    value={password}
                    onChange={(e) => { setError(""); setPassword(e.target.value); }}
                    placeholder="At least 6 characters"
                    minLength={6}
                    required
                  />
                </div>
                {mode === "register" && (
                  <label
                    htmlFor="tos-accept"
                    className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed cursor-pointer select-none"
                    data-testid="auth-tos-label"
                  >
                    <input
                      id="tos-accept"
                      type="checkbox"
                      checked={tosAccepted}
                      onChange={(e) => setTosAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary/40"
                      data-testid="auth-tos-checkbox"
                      required
                    />
                    <span>
                      I have read and agree to the{" "}
                      <Link to="/trust" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">
                        Terms of Service
                      </Link>
                      {" "}and{" "}
                      <Link to="/trust" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">
                        Privacy Policy
                      </Link>
                      . I can download them anytime from the Trust Center.
                    </span>
                  </label>
                )}
                <Button
                  type="submit"
                  data-testid={mode === "login" ? TEST_IDS.loginSubmit : TEST_IDS.registerSubmit}
                  disabled={loading || (mode === "register" && !tosAccepted)}
                  aria-busy={loading}
                  className="w-full rounded-full h-11 text-base font-semibold active:scale-[0.98] transition-transform"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mode === "login" ? "Signing in…" : "Creating account…"}
                    </>
                  ) : (
                    mode === "login" ? "Sign in" : "Create account"
                  )}
                </Button>
                {error && !loading && (
                  <div
                    role="alert"
                    data-testid="auth-error"
                    className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </form>
              <div className="mt-6 text-sm text-center text-muted-foreground">
                {mode === "login" ? "New here?" : "Already have an account?"}{" "}
                <button
                  type="button"
                  data-testid={mode === "login" ? TEST_IDS.switchToRegister : TEST_IDS.switchToLogin}
                  className="underline underline-offset-2 hover:text-primary transition-colors"
                  onClick={() => { setError(""); setMode(mode === "login" ? "register" : "login"); }}
                >
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
